import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Loading from '../components/Loading';
import useABTest from '../hooks/useABTest';
import useUser from '../hooks/useUser';
import { navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';
import {
  buildImmediateCheckoutPath,
  completePostSignupJourney,
  hasActivePostSignupJourney,
  POST_SIGNUP_CHECKOUT_EXPERIMENT,
  sanitizeInternalDestination,
} from '../utils/postSignupJourney';

const PREVIEW_HOSTS = new Set(['localhost', '127.0.0.1', 'dev.botspot.trade', 'stg.botspot.trade']);

export const isPostSignupPreviewHost = (hostname: string): boolean => PREVIEW_HOSTS.has(hostname);

interface PostSignupExperimentResult {
  variant: string | boolean | null;
  isLoading: boolean;
}

interface PostSignupUserIdentity {
  id?: string | number;
  howFoundUs?: string | null;
}

function PostSignupExperimentDecision({
  destination,
  forcedVariant,
}: {
  destination: string;
  forcedVariant: string | null;
}) {
  const navigate = useNavigate();
  const safeForcedVariant =
    forcedVariant === 'test' || forcedVariant === 'control' ? forcedVariant : null;
  const trackingData = useMemo(
    () => ({
      source: '/post-signup',
      destination_path: destination.split('?')[0],
      validVariants: ['control', 'test'],
      skipFeatureFlagEvaluation: Boolean(safeForcedVariant),
      forcedVariant: safeForcedVariant,
      forcedExposureValue: safeForcedVariant,
      trackForcedExposure: Boolean(safeForcedVariant),
    }),
    [destination, safeForcedVariant],
  );
  // SAFETY: The legacy hook returns this stable result contract on every execution path.
  const { variant, isLoading } = useABTest(
    POST_SIGNUP_CHECKOUT_EXPERIMENT.name,
    POST_SIGNUP_CHECKOUT_EXPERIMENT.featureFlag,
    'control',
    trackingData,
  ) as PostSignupExperimentResult;

  useEffect(() => {
    if (isLoading) return;
    if (variant === 'test') {
      const backgroundUrl = new URL(destination, window.location.origin);
      navigateToCheckoutOverlay(
        navigate,
        buildImmediateCheckoutPath(destination),
        {
          pathname: backgroundUrl.pathname,
          search: backgroundUrl.search,
          hash: backgroundUrl.hash,
          state: null,
          key: 'post-signup-destination',
        },
        { replace: true },
      );
    } else {
      navigate(destination, { replace: true });
    }
    // Keep the journey marker through the navigation commit. Landing-page
    // routing effects use it to stand down after a newly created account. If
    // it is removed before React unmounts the acquisition page, that older
    // effect can overwrite this decision and strip experiment parameters.
    window.setTimeout(() => completePostSignupJourney({ preserveRoutingGuard: true }), 0);
  }, [destination, isLoading, navigate, variant]);

  return <Loading />;
}

export default function PostSignupDecisionPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, user, userProfile } = useUser();
  // SAFETY: The legacy user hook supplies the authenticated BotSpot identity fields used here.
  const resolvedUserProfile = userProfile as PostSignupUserIdentity | null;
  // SAFETY: The legacy auth context supplies the same owner identifier contract.
  const authenticatedUser = user as PostSignupUserIdentity | null;
  const [authGraceExpired, setAuthGraceExpired] = useState(false);
  const destination = sanitizeInternalDestination(searchParams.get('redirectTo'));
  const forcedCheckoutVariant = isPostSignupPreviewHost(window.location.hostname)
    ? searchParams.get('postSignupCheckoutVariant')
    : null;
  const hasForcedPreviewDecision =
    forcedCheckoutVariant === 'test' || forcedCheckoutVariant === 'control';
  const attributionReadbackConfirmed = searchParams.get('attribution_completed') === 'true';

  useEffect(() => {
    if (isAuthenticated) {
      setAuthGraceExpired(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setAuthGraceExpired(true), 4000);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated]);

  let authPending = false;
  try {
    authPending = window.sessionStorage.getItem('botspot_post_signup_auth_pending') === 'true';
  } catch {
    authPending = false;
  }

  if (isLoading || (!isAuthenticated && (!authGraceExpired || authPending))) return <Loading />;
  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        replace
        to={`/login?returnTo=${encodeURIComponent(returnTo)}&source=post_signup_decision`}
      />
    );
  }

  if (
    !hasActivePostSignupJourney(resolvedUserProfile?.id || authenticatedUser?.id) &&
    !(hasForcedPreviewDecision && attributionReadbackConfirmed)
  ) {
    return <Navigate replace to={destination} />;
  }

  // The forced survey adds attribution_completed only after both the save
  // response and an authenticated profile readback confirm the persisted
  // value. Honor that handoff if this hook instance is still rendering an
  // older profile snapshot. The active journey check above keeps this marker
  // scoped to a newly created product account.
  if (!String(resolvedUserProfile?.howFoundUs || '').trim() && !attributionReadbackConfirmed) {
    const attributionParams = new URLSearchParams({
      forced_attribution: 'true',
      source_flow: 'post_signup_decision',
      redirectTo: destination,
    });
    if (forcedCheckoutVariant === 'test' || forcedCheckoutVariant === 'control') {
      attributionParams.set('postSignupCheckoutVariant', forcedCheckoutVariant);
    }
    return <Navigate replace to={`/signup-survey?${attributionParams.toString()}`} />;
  }

  return (
    <PostSignupExperimentDecision destination={destination} forcedVariant={forcedCheckoutVariant} />
  );
}
